import { getBillPaymentWechatFormConfig } from '@/api/clearing/billPayment'
import { getCacheListByKey } from '@/utils/basicDataCache'
import { basicCacheKeys } from '@/utils/cacheConfig'

// 企微付款申请相关的通用逻辑
// REQ-0033 数据源后移：字段配置（titles/options/required/optionsSource）由后端按
// type+companyCode 清洗下发（act-service /billPayment/wechatFormConfig），
// 前端不再解析企微模板 schema；仅保留表单交互逻辑与 LOCAL_USER_CACHE 本地数据源例外（水单通知人）。
export default {
  data() {
    return {
      // 后端清洗后的企微字段配置（含 titles/options/required/optionsSource）
      enterpriseWechatFormFields: [],
      enterpriseWechatFormConfigLoading: false
    }
  },
  methods: {
    // REQ-0033：按付款类型 + 当前公司（右上角）取后端清洗后的企微表单配置
    async loadEnterpriseWechatFormConfig() {
      const paymentType = this.billPayment && this.billPayment.paymentType
      if (paymentType === undefined || paymentType === null || paymentType === '') {
        this.enterpriseWechatFormFields = []
        return
      }
      const officeCode = (this.$store.getters.currentOffice || {}).code || ''
      this.enterpriseWechatFormConfigLoading = true
      try {
        const res = await getBillPaymentWechatFormConfig({ type: String(paymentType), companyCode: officeCode })
        const data = (res && res.data) || {}
        const fields = Array.isArray(data.fields) ? data.fields : []
        // LOCAL_USER_CACHE 数据源例外（当前仅水单通知人）：options 由前端本地用户缓存填充
        fields.forEach(field => {
          if (field && field.optionsSource === 'LOCAL_USER_CACHE') {
            const localOptions = field.prop === 'waterNotifier' ? this.getEnterpriseWechatWaterNotifierOptions() : []
            this.$set(field, 'options', localOptions)
          }
        })
        this.enterpriseWechatFormFields = fields
      } catch (error) {
        // fail-open：取数失败清空字段区，弹窗可用不崩
        console.error('[EnterpriseWechat] load form config failed:', error)
        this.enterpriseWechatFormFields = []
      } finally {
        this.enterpriseWechatFormConfigLoading = false
        this.normalizeAllEnterpriseWechatFieldValues()
      }
      // 所在部门：下拉选项实时取自企微通讯录部门列表（后端 wechatFormConfig 清洗下发），失败时保持模板选项
      this.loadLocatedDepartmentOptions()
    },
    // 加载「所在部门」下拉选项（数据源：企微平台部门列表，经 /billPayment/wechatFormConfig 下发）
    async loadLocatedDepartmentOptions() {
      const paymentType = this.billPayment && this.billPayment.paymentType
      if (paymentType === undefined || paymentType === null || paymentType === '') {
        return
      }
      const locatedField = (this.enterpriseWechatFields || []).find(field => field && field.prop === 'locatedDepartment')
      if (!locatedField) {
        return
      }
      // 已保存值（详情/保存接口返回的 locatedDepartment/locatedDepartmentId）：有值就必须显示，不因数据源为空而隐藏
      const savedName = this.billPayment.locatedDepartment === undefined || this.billPayment.locatedDepartment === null ? '' : String(this.billPayment.locatedDepartment).trim()
      const savedId = this.billPayment.locatedDepartmentId === undefined || this.billPayment.locatedDepartmentId === null ? '' : String(this.billPayment.locatedDepartmentId).trim()
      const hasSavedValue = !!(savedName || savedId)
      // 乐观同步置位：切换付款类型时立即跟随其他字段切换显隐，不等数据源接口返回（接口返回后再按结果修正）
      this.$set(locatedField, 'hidden', !hasSavedValue)
      try {
        const res = await getBillPaymentWechatFormConfig({ type: String(paymentType) })
        const config = (res && res.data) || {}
        const locatedConfig = (config.fields || []).find(field => field && field.prop === 'locatedDepartment')
        if (locatedConfig && Array.isArray(locatedConfig.options) && locatedConfig.options.length > 0) {
          // 后端已下发（当前用户在企微有多个部门）：显示字段并填充选项
          this.$set(locatedField, 'hidden', false)
          this.$set(locatedField, 'options', locatedConfig.options.map(option => ({
            label: option.label,
            value: option.value,
            // 企微部门 id（提交审批时作为企微 choose_department 提单部门）
            departmentId: option.departmentId === undefined || option.departmentId === null ? '' : String(option.departmentId)
          })))
        } else if (hasSavedValue) {
          // 数据源无数据但已有保存值：仍需显示，用已存值兜底渲染（保证详情回显与再次提交 choose_department）
          this.$set(locatedField, 'hidden', false)
          this.$set(locatedField, 'options', [{
            label: savedName || savedId,
            value: savedName || savedId,
            departmentId: savedId
          }])
        } else {
          // 新建且数据源无数据（当前用户无企微部门/仅一个部门）：隐藏字段并清空残留值，避免空数据展示
          this.$set(locatedField, 'hidden', true)
          this.$set(locatedField, 'options', [])
          if (this.billPayment) {
            this.$set(this.billPayment, 'locatedDepartment', '')
            this.$set(this.billPayment, 'locatedDepartmentId', '')
          }
        }
      } catch (error) {
        // fail-open：接口失败恢复显示，不因网络问题隐藏功能
        this.$set(locatedField, 'hidden', false)
        console.error('[EnterpriseWechat] load located department options failed:', error)
      }
    },
    // 字段 label：从后端透传的多语种 titles 按当前语言取（zh→zh_CN，其余→en），未命中回落首个标题；
    // 展示层剥掉尾部全角/半角冒号（企微模板控件标题自带「：」，旧 i18n 文案无冒号，避免 95px label-width 下换行）
    getEnterpriseWechatLabel(field) {
      if (!field || !Array.isArray(field.titles) || field.titles.length === 0) {
        return ''
      }
      const preferLang = this.$store.getters.language === 'zh' ? 'zh_CN' : 'en'
      const matched = field.titles.find(t => t && t.lang === preferLang && t.text)
      const text = (matched && matched.text) || (field.titles[0] && field.titles[0].text) || ''
      return text.replace(/[：:]+\s*$/, '').trim()
    },
    getEnterpriseWechatWaterNotifierOptions() {
      if (this.enterpriseWechatWaterNotifierOptions && this.enterpriseWechatWaterNotifierOptions.length > 0) {
        return this.enterpriseWechatWaterNotifierOptions
      }
      const users = getCacheListByKey(basicCacheKeys.basic_data_User) || []
      const options = users
        .map(user => {
          const chineseName = user.chineseName || ''
          const englishName = user.englishName || ''
          const userName = user.userName || user.name || ''
          const label = this.$selectByLang(chineseName, englishName) || userName || ''
          if (!label) {
            return null
          }
          const externalCode = user.externalCode || user.external_code || user.enterpriseWechatExternalCode
          const userId = user.userId
          // 水单通知人：保存时统一使用 userId（字符串），回显时用 value(userId) 匹配 label(用户名)
          const value = userId === undefined || userId === null ? '' : String(userId)
          if (!value) {
            return null
          }
          return {
            label,
            value,
            userName,
            chineseName,
            englishName,
            userId,
            externalCode: externalCode || '',
            raw: user
          }
        })
        .filter(item => item)
      this.enterpriseWechatWaterNotifierOptions = options
      return options
    },
    normalizeAllEnterpriseWechatFieldValues() {
      this.enterpriseWechatFormFields.forEach(field => this.normalizeEnterpriseWechatFieldValue(field))
    },
    normalizeEnterpriseWechatFieldValue(fieldConfig) {
      if (!this.billPayment || !fieldConfig || !fieldConfig.prop) {
        return
      }
      const prop = fieldConfig.prop
      if (!this.billPayment.hasOwnProperty(prop)) {
        this.$set(this.billPayment, prop, '')
        return
      }
      const value = this.billPayment[prop]
      if (value === undefined || value === null) {
        this.$set(this.billPayment, prop, '')
        return
      }
      // 水单通知人字段：统一保存 userId（字符串）
      if (prop === 'waterNotifier') {
        const picked = Array.isArray(value) ? (value.length > 0 ? value[0] : '') : value
        const raw = String(picked).trim()
        if (!raw) {
          this.$set(this.billPayment, prop, '')
          return
        }
        // 只认 userId（通常为纯数字）。不再兼容历史“用户名”存储。
        this.$set(this.billPayment, prop, /^\d+$/.test(raw) ? raw : '')
        return
      }
      // 其他字段：统一按单选取首个
      if (Array.isArray(value)) {
        this.$set(this.billPayment, prop, value.length > 0 ? value[0] : '')
      }
    },
    hasEnterpriseWechatFieldValue(fieldConfig = {}) {
      if (!fieldConfig || !fieldConfig.prop || !this.billPayment) {
        return true
      }
      const value = this.billPayment[fieldConfig.prop]
      return value !== undefined && value !== null && value !== ''
    },
    buildBillPaymentPayload(source) {
      const billPayment = source || this.billPayment || {}
      const payload = { ...billPayment }
      if (Array.isArray(this.enterpriseWechatFormFields) && this.enterpriseWechatFormFields.length > 0) {
        this.enterpriseWechatFormFields.forEach(fieldConfig => {
          if (!fieldConfig || !fieldConfig.prop) {
            return
          }
          const prop = fieldConfig.prop
          let value = billPayment[prop]
          if (value === undefined || value === null) {
            return
          }
          if (Array.isArray(value)) {
            value = value.length > 0 ? value[0] : ''
          }
          // 水单通知人：保存时统一使用 userId（字符串）
          if (prop === 'waterNotifier') {
            const raw = value === undefined || value === null ? '' : String(value).trim()
            value = raw && /^\d+$/.test(raw) ? raw : ''
          }
          payload[prop] = value
        })
      }
      return payload
    },
    handleEnterpriseWechatSelectCallback(fieldConfig, payload) {
      if (!fieldConfig || !fieldConfig.prop) {
        return
      }
      let value = payload ? (payload.tagValue || payload.value || '') : ''
      if (fieldConfig.prop === 'waterNotifier') {
        value = value === undefined || value === null ? '' : String(value).trim()
      }
      this.$set(this.billPayment, fieldConfig.prop, value || '')
      // 企微「付款公司」为用户主动切换：直调我方抬头匹配并允许未匹配清空（keepWhenUnmatched: false）；
      // 回显/初始化赋值走 paymentCompany watcher（keepWhenUnmatched: true，未匹配保留）
      if (fieldConfig.prop === 'paymentCompany' && typeof this.syncOurTitleByPaymentCompany === 'function') {
        this.syncOurTitleByPaymentCompany(value || '', { keepWhenUnmatched: false })
      }
      // 所在部门：同步保存企微部门 id 到 locatedDepartmentId（提交审批时作企微 choose_department）
      if (fieldConfig.prop === 'locatedDepartment') {
        const selectedValue = Array.isArray(value) ? (value.length > 0 ? value[value.length - 1] : '') : value
        const matched = (fieldConfig.options || []).find(option => option && option.value === selectedValue)
        const departmentId = matched && matched.departmentId !== undefined && matched.departmentId !== null ? String(matched.departmentId) : ''
        this.$set(this.billPayment, 'locatedDepartmentId', departmentId)
      }
      this.handleEnterpriseWechatSelectChange(fieldConfig, value)
    },
    handleEnterpriseWechatSelectChange(fieldConfig, value) {
      if (!fieldConfig || fieldConfig.prop !== 'waterNotifier') {
        return
      }
      const selectedValue = Array.isArray(value) ? value[value.length - 1] : value
      const selectedKey = selectedValue === undefined || selectedValue === null ? '' : String(selectedValue).trim()
      if (!selectedKey) {
        return
      }
      const options = this.getEnterpriseWechatWaterNotifierOptions()
      const selectedOption = options.find(option => option && option.value === selectedKey)
      if (selectedOption && !selectedOption.externalCode) {
        this.$message.warning(this.$t('Payment.enterpriseWechatWaterNotifierMissing'))
        this.$nextTick(() => {
          this.billPayment.waterNotifier = ''
        })
      }
    },
    setDefaultWaterNotifier() {
      if (!this.billPayment) {
        return
      }
      const currentValue = this.billPayment.waterNotifier
      if (currentValue) {
        return
      }
      const userInfo = this.$store.getters.userInfo || {}
      const externalCode = userInfo.externalCode || userInfo.external_code || userInfo.enterpriseWechatExternalCode
      if (!externalCode) {
        return
      }
      const notifierOptions = this.getEnterpriseWechatWaterNotifierOptions()
      const userId = userInfo.userId === undefined || userInfo.userId === null ? '' : String(userInfo.userId)
      const matchedOption = notifierOptions.find(option => option && option.value === userId)
      const defaultValue = matchedOption ? matchedOption.value : userId
      if (defaultValue) {
        this.$set(this.billPayment, 'waterNotifier', defaultValue)
      }
    }
  }
}
